import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, FormGroup, TextField } from '@mui/material';
import userProfile from '../../../../assets/images/userProfile.svg';
import { useForm } from 'react-hook-form';
import { useAlert } from '../../../../components/Notification';
import profileDelete from '../../../../assets/images/profileDeleteIcon.svg';
import CropComponent from '../../../../components/crop';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { UrlToBase } from '../../../../components/urlToBase64';
import { useHook as ControllerHook } from './useHook';

const ControllerNameEdit = (props: any) => {
  const { id } = useParams();
  const { editController } = ControllerHook();
  const {
    editData,
    handleCreateSubmit,
    setCreateCroppedImages,
    handleCloseEdit,
  } = props;
  const navigate = useNavigate();
  const [profileError, setProfileError] = useState('');
  const [croppedImage, setCroppedImages] = useState<string | null>(null);
  const [opencrop, setOpenCrop] = useState(false);
  const showAlert = useAlert();
  const [isActive, setIsActive] = useState(false);
  const handleActiveChange = () => {
    setIsActive(true);
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    defaultValues: {
      name: editData ? editData?.controllerName : '',
      model: editData ? editData?.model : '',
    },
  });
  const [uploadError, setUploadError] = useState('');

  const submitdata = async (data: any) => {
    if (opencrop) {
      setUploadError('Please upload the image');
    } else {
      if (editData) {
        const imageType = croppedImage?.substring(
          11,
          croppedImage?.indexOf(';'),
        );
        const imageWithoutPrefix = croppedImage?.substring(
          croppedImage?.indexOf(',') + 1,
        );

        await editController({
          controllerID: id,
          controllerName: data?.name,
          model: data?.model,
          image: imageWithoutPrefix ? imageWithoutPrefix : '',
          ext: imageType ? imageType : '',
          parameters: editData?.parameters,
        });
        navigate(
          `/hardware/controller/edit/${data?.name ? data?.name : ' '}/${id}`,
        );
        handleCloseEdit();
      } else {
        handleCreateSubmit(data);
      }
    }
  };

  const handleCropComplete = (croppedImage: string) => {
    setCroppedImages(croppedImage);
    setOpenCrop(false);
    setUploadError('');
  };
  useEffect(() => {
    if (!editData) {
      setCreateCroppedImages(croppedImage);
    }
  }, [croppedImage]);

  const validateImage = (
    file: File,
    setProfileError: (error: string) => void,
  ): boolean => {
    const allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/gif'];
    const maxFileSize: number = 1024 * 1024; // 1MB

    if (!allowedTypes.includes(file.type)) {
      setProfileError('Please select a JPG, GIF, or PNG file.');
      return false;
    }

    if (file.size > maxFileSize) {
      setProfileError('File size exceeds the 1MB limit.');
      return false;
    }

    return true;
  };
  const toBase64 = (file: Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file as Data URL'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file: File | null = e.target.files ? e.target.files[0] : null;

    if (file && validateImage(file, setProfileError)) {
      setProfileError('');

      await toBase64(file)
        .then((base64String) => {
          // setImageString(base64String as string);
          setCroppedImages(base64String as string);
          setOpenCrop(true);
        })
        .catch(() => {
          // setImageString('');
          setCroppedImages(null);
          showAlert(`Failed to Upload Image.`, 'error');
          setOpenCrop(false);
        });
    }
    setIsActive(true);
  };
  const handleDeleteImage = () => {
    // setImageString('');
    setCroppedImages(null);
    const uploadInput = document.getElementById(
      'upload-button',
    ) as HTMLInputElement;
    if (uploadInput) {
      uploadInput.value = '';
    }
    setIsActive(true);
    setUploadError('');
  };

  useEffect(() => {
    if (editData) {
      const imageUrl = editData?.image;
      if (imageUrl) {
        UrlToBase(imageUrl)
          .then((base64String) => {
            setCroppedImages(base64String);
          })
          .catch((error) => console.error(error));
      }
    }
  }, []);

  return (
    <div className="create_controller_form">
      <form onSubmit={handleSubmit(submitdata)}>
        <div className="create_controller_form_content">
          <div className="create_controller_form_label">
            <h3>Name</h3>
          </div>
          <div className="create_controller_form_field">
            <div className="create_controller_form_field-name">
              <FormGroup>
                <TextField
                  id="name"
                  variant="outlined"
                  {...register('name', { required: true })}
                  error={errors.name ? true : false}
                  onChange={() => {
                    handleActiveChange();
                    clearErrors('name');
                  }}
                  // disabled={editData ? true : false}
                />
              </FormGroup>
            </div>
            {errors.name && (
              <span className="text-danger"> Name is required</span>
            )}
          </div>
        </div>
        <div className="create_controller_form_content">
          <div className="create_controller_form_label">
            <h3>Image</h3>
          </div>
          <div className="create_controller_form_field">
            <div className="create_user_profile-container">
              <div className="create_user_profile">
                <div>
                  {croppedImage ? (
                    <img src={croppedImage} width={72} alt="User Profile" />
                  ) : (
                    <img src={userProfile} width={72} alt="User Profile" />
                  )}
                </div>
                <div>
                  <Button
                    variant="outlined"
                    component="label"
                    htmlFor="upload-button"
                    className="custom-file-upload"
                  >
                    Change
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    id="upload-button"
                    onChange={(e) => handleFileChange(e)}
                  />
                </div>
                <div className="imageformat">
                  <p>JPG, GIF, or PNG. 1MB Max.</p>
                </div>
              </div>
              {croppedImage && (
                <Button
                  className="create_user_profile-delete"
                  onClick={() => handleDeleteImage()}
                >
                  <img src={profileDelete} width={44} alt="delete icon" />
                </Button>
              )}
            </div>
            {profileError && (
              <span className="text-danger">{profileError}</span>
            )}
            {opencrop && croppedImage && (
              <CropComponent
                srce={croppedImage}
                onCropComplete={handleCropComplete}
                uploadError={uploadError}
              />
            )}
          </div>
        </div>
        <div className="create_controller_form_content">
          <div className="create_controller_form_label">
            <h3>Model</h3>
          </div>
          <div className="create_controller_form_field">
            <FormGroup>
              <TextField
                id="model"
                variant="outlined"
                fullWidth
                {...register('model', { required: true })}
                error={errors.model ? true : false}
                onChange={() => {
                  handleActiveChange();
                  clearErrors('model');
                }}
                // disabled={editData ? true : false}
              />
            </FormGroup>
            {errors.model && (
              <span className="text-danger"> model is required</span>
            )}
          </div>
        </div>
        <div className="create_controller_form-submit">
          <Button
            onClick={() => (handleCloseEdit ? handleCloseEdit() : navigate(-1))}
            className="create_controller_form-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className={
              isActive
                ? 'create_controller_form-submit-btn'
                : 'disable_controller-btn'
            }
            disabled={!isActive}
          >
            {editData ? 'Save Changes' : 'Next'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const MemoizedComponent = React.memo(ControllerNameEdit);
export default MemoizedComponent;
